// Timesheets (round 44, spec 13). HOURS ONLY: nothing here knows a rate.
//
// The page never decides what is allowed: the detail response carries `can`
// and `allowedStatuses`, and the buttons follow them. Totals are the server's,
// and the live preview while typing comes from lib/timesheetHours.ts, a
// byte-for-byte copy of the server's calculator.
import { postForm, request } from './api';
import type { WeekTotals } from './lib/timesheetHours';

export type TimesheetStatus = 'draft' | 'submitted' | 'returned' | 'approved';
export type BoardStatus = TimesheetStatus | 'missing';

export const TIMESHEET_STATUS_LABEL: Record<BoardStatus, string> = {
  missing: 'Missing',
  draft: 'Draft',
  submitted: 'Submitted',
  returned: 'Sent back',
  approved: 'Approved',
};

export interface TimesheetSheet {
  id: string;
  userId: string;
  weekStart: string;
  weekEnd: string;
  weekLabel: string;
  status: TimesheetStatus;
  lockVersion: number;
  note: string | null;
  signed: boolean;
  signedAt: string | null;
  signedByName: string | null;
  officeEntered: boolean;
  createdByName: string;
  submittedAt: string | null;
  submittedByName: string | null;
  approvedAt: string | null;
  approvedByName: string | null;
  returnedAt: string | null;
  returnedByName: string | null;
  returnReason: string | null;
  updatedAt: string;
}

export interface TimesheetDay {
  id: string;
  date: string;
  startTime: string | null;
  finishTime: string | null;
  breakMinutes: number;
  finishesNextDay: boolean;
  workedMinutes: number;
  jobId: string | null;
  jobNumber: string | null;
  jobTitle: string | null;
  location: string | null;
  leaveTypeId: string | null;
  leaveMinutes: number | null;
  allowanceTypeIds: string[];
  note: string | null;
}

/** What a save sends for one day. */
export interface DayInput {
  date: string;
  startTime: string | null;
  finishTime: string | null;
  breakMinutes: number;
  jobId: string | null;
  location: string | null;
  leaveTypeId: string | null;
  leaveMinutes: number | null;
  allowanceTypeIds: string[];
  note: string | null;
}

export interface TimesheetEvent {
  id: string;
  kind: 'created' | 'edited' | 'submitted' | 'approved' | 'returned' | 'file_added' | 'file_removed';
  fromStatus: TimesheetStatus | null;
  toStatus: TimesheetStatus | null;
  detail: {
    changes?: { date: string; field: string; before?: unknown; after?: unknown; changed?: boolean }[];
    signed?: boolean;
    onBehalf?: boolean;
    filename?: string;
  };
  reason: string | null;
  actorName: string;
  at: string;
}

export interface TypeRow {
  id: string;
  name: string;
  sortOrder: number;
  archived: boolean;
}

export interface JobOption {
  id: string;
  number: string;
  title: string;
}

export interface TimesheetCan {
  edit: boolean;
  submit: boolean;
  submitForWorker: boolean;
  approve: boolean;
  return: boolean;
  uploadFile: boolean;
  downloadPdf: boolean;
  delete: boolean;
}

export interface TimesheetDetail {
  sheet: TimesheetSheet;
  /** The same person's week before, if they have one. */
  previousWeekId: string | null;
  documentCount: number;
  worker: { id: string; name: string; position: string | null };
  days: TimesheetDay[];
  totals: WeekTotals;
  events: TimesheetEvent[];
  can: TimesheetCan;
  allowedStatuses: TimesheetStatus[];
  leaveTypes: TypeRow[];
  allowanceTypes: TypeRow[];
  jobOptions: { byDate: Record<string, JobOption[]>; others: JobOption[] };
  nonWorkingDays: { date: string; name: string; kind: string }[];
  nonWorkingWeekdays: number[];
}

export interface MineWeek extends TimesheetSheet {
  totals: WeekTotals;
}

export interface MineSummary {
  onTimesheets: boolean;
  thisWeekStart: string;
  lastWeekStart: string;
  weeks: MineWeek[];
  missing: string[];
}

export interface BoardRow {
  userId: string;
  name: string;
  position: string | null;
  weekId: string | null;
  lockVersion: number | null;
  status: BoardStatus;
  signed: boolean;
  officeEntered: boolean;
  totals: WeekTotals | null;
  submittedAt: string | null;
  updatedAt: string | null;
}

export interface Board {
  weekStart: string;
  weekEnd: string;
  weekLabel: string;
  thisWeekStart: string;
  /** What the board shows when no week is asked for. */
  lastWeekStart: string;
  rows: BoardRow[];
  counts: Record<BoardStatus | 'all', number>;
  /** Other weeks with sheets waiting for approval. */
  awaitingElsewhere: { weekStart: string; weekLabel: string; count: number }[];
}

function typesApi(base: string) {
  return {
    list: (archived = false) =>
      request<{ types: TypeRow[] }>(`${base}${archived ? '?archived=true' : ''}`),
    create: (name: string) => request<{ type: TypeRow }>(base, { method: 'POST', body: { name } }),
    rename: (id: string, name: string) =>
      request<{ type: TypeRow }>(`${base}/${id}`, { method: 'PATCH', body: { name } }),
    archive: (id: string, archived: boolean) =>
      request<{ type: TypeRow }>(`${base}/${id}/archive`, { method: 'POST', body: { archived } }),
    remove: (id: string) => request<void>(`${base}/${id}`, { method: 'DELETE' }),
  };
}

export const timesheets = {
  mine: () => request<MineSummary>('/timesheets/mine'),
  createWeek: (weekStart: string, userId?: string) =>
    request<TimesheetDetail>('/timesheets/weeks', {
      method: 'POST',
      body: { weekStart, ...(userId ? { userId } : {}) },
    }),
  detail: (id: string) => request<TimesheetDetail>(`/timesheets/${id}`),
  saveDays: (id: string, body: { lockVersion: number; days: DayInput[]; note?: string | null }) =>
    request<TimesheetDetail>(`/timesheets/${id}/days`, { method: 'PUT', body }),
  /** The signature travels in the submit itself; the office sends none. */
  submit: (id: string, lockVersion: number, signature: Blob | null) => {
    const form = new FormData();
    form.append('lockVersion', String(lockVersion));
    if (signature) form.append('file', signature, 'signature.png');
    return postForm<TimesheetDetail>(`/timesheets/${id}/submit`, form);
  },
  approve: (id: string, lockVersion: number) =>
    request<TimesheetDetail>(`/timesheets/${id}/approve`, { method: 'POST', body: { lockVersion } }),
  sendBack: (id: string, lockVersion: number, reason: string) =>
    request<TimesheetDetail>(`/timesheets/${id}/return`, {
      method: 'POST',
      body: { lockVersion, reason },
    }),
  remove: (id: string) => request<void>(`/timesheets/${id}`, { method: 'DELETE' }),
  board: (week?: string) =>
    request<Board>(`/timesheets/board${week ? `?week=${encodeURIComponent(week)}` : ''}`),
  pdfUrl: (id: string, download = false) =>
    `/api/timesheets/${id}/pdf${download ? '?download=1' : ''}`,
  exportUrl: (options: { from: string; to: string; detail: 'days' | 'weeks'; approvedOnly: boolean }) => {
    const params = new URLSearchParams({ from: options.from, to: options.to, detail: options.detail });
    if (options.approvedOnly) params.set('approvedOnly', '1');
    return `/api/timesheets/export.csv?${params.toString()}`;
  },
  leaveTypes: typesApi('/leave-types'),
  allowanceTypes: typesApi('/allowance-types'),
};

/** A day as the save sends it. */
export function toInput(day: TimesheetDay): DayInput {
  return {
    date: day.date,
    startTime: day.startTime,
    finishTime: day.finishTime,
    breakMinutes: day.breakMinutes,
    jobId: day.jobId,
    location: day.location,
    leaveTypeId: day.leaveTypeId,
    leaveMinutes: day.leaveMinutes,
    allowanceTypeIds: day.allowanceTypeIds,
    note: day.note,
  };
}
